import { useId } from 'react';

/** One bar, or a pair when the chart compares two series (Sales and Takings). */
export interface ChartBar {
  key: string;
  label: string;
  value: number;
  value2?: number;
}

const W = 600;
const H = 180;

/**
 * A plain bar chart drawn in the page (ADR-043, D5): no library, no animation. Its numbers are always in a table: the
 * table the page shows right under it (tableId), or one of its own behind "Show the numbers". Negative bars hang below
 * a zero line. Two series are told apart by a legend in words as well as by colour.
 */
export function BarChart({
  title,
  bars,
  format,
  series,
  tableId,
  labelHeader = 'Day',
  testId,
}: {
  title: string;
  bars: ChartBar[];
  format: (v: number) => string;
  /** The series' names, e.g. ['Sales', 'Takings']; a second name draws pairs from value2. */
  series: [string] | [string, string];
  /** The id of a table on the page holding exactly these numbers. */
  tableId?: string;
  labelHeader?: string;
  testId?: string;
}) {
  const ownTable = useId();
  const two = series.length === 2;
  const values = bars.flatMap((b) => (two ? [b.value, b.value2 ?? 0] : [b.value]));
  const lo = Math.min(0, ...values);
  const hi = Math.max(0, ...values);
  const span = hi - lo || 1;
  const y = (v: number) => (H * (hi - v)) / span;
  const y0 = y(0);
  const slot = W / Math.max(bars.length, 1);
  const barW = two ? slot * 0.38 : slot * 0.7;
  const rect = (v: number, x: number, cls: string, name: string, label: string) => {
    const top = Math.min(y(v), y0);
    const h = Math.abs(y(v) - y0);
    return (
      <rect
        key={cls}
        className={cls}
        x={x}
        y={v === 0 ? y0 : top}
        width={barW}
        height={v === 0 ? 0 : Math.max(h, 1)}
      >
        <title>{`${label}: ${name} ${format(v)}`}</title>
      </rect>
    );
  };
  // at most four labels under the chart, evenly spread, always the first and the last (they fit a phone)
  const step = Math.max(1, Math.ceil((bars.length - 1) / 3));
  const ticks = bars.filter((_, i) => i % step === 0 || i === bars.length - 1);
  const peak = bars.reduce<ChartBar | null>((m, b) => (!m || b.value > m.value ? b : m), null);
  const summary = peak
    ? `${bars.length} ${bars.length === 1 ? 'bar' : 'bars'}; the highest ${series[0].toLowerCase()} is ${format(peak.value)} (${peak.label}).`
    : 'Nothing to show.';
  return (
    <figure className="rp-chart" data-testid={testId}>
      {two && (
        <figcaption className="rp-chart__legend">
          <span>
            <i className="rp-swatch rp-swatch--1" aria-hidden /> {series[0]} (solid, left)
          </span>
          <span>
            <i className="rp-swatch rp-swatch--2" aria-hidden /> {series[1]} (outlined, right)
          </span>
        </figcaption>
      )}
      <div className="rp-chart__scale" aria-hidden>
        <span>{format(hi)}</span>
      </div>
      <svg
        className="rp-chart__svg"
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={`${title}. ${summary}`}
        aria-details={tableId ?? ownTable}
      >
        <line className="rp-chart__zero" x1={0} x2={W} y1={y0} y2={y0} />
        {bars.map((b, i) => {
          const x = i * slot + (slot - (two ? barW * 2 + slot * 0.04 : barW)) / 2;
          return (
            <g key={b.key}>
              {rect(b.value, x, 'rp-bar rp-bar--1', series[0], b.label)}
              {two &&
                rect(
                  b.value2 ?? 0,
                  x + barW + slot * 0.04,
                  'rp-bar rp-bar--2',
                  series[1]!,
                  b.label,
                )}
            </g>
          );
        })}
      </svg>
      {lo < 0 && (
        <div className="rp-chart__scale" aria-hidden>
          <span>{format(lo)}</span>
        </div>
      )}
      <div className="rp-chart__axis" aria-hidden>
        {ticks.map((b) => (
          <span key={b.key}>{b.label}</span>
        ))}
      </div>
      {!tableId && (
        <details className="rp-chart__numbers">
          <summary>Show the numbers</summary>
          <div className="d-table-scroll">
            <table className="d-table" id={ownTable}>
              <caption className="d-sr-only">{title}</caption>
              <thead>
                <tr>
                  <th scope="col">{labelHeader}</th>
                  {series.map((s) => (
                    <th scope="col" key={s} className="d-num">
                      {s}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {bars.map((b) => (
                  <tr key={b.key}>
                    <td>{b.label}</td>
                    <td className="d-num d-mono">{format(b.value)}</td>
                    {two && <td className="d-num d-mono">{format(b.value2 ?? 0)}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </figure>
  );
}
